import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactSVG from 'react-svg';
import { IconButton, CircularProgress } from '@material-ui/core';
import * as lib from '~/dependencies';
import { usePlayRecord } from '~/hooks';
import api from '~/services';
import * as actions from '~/store/actions';
import { log, dateParse, getDetailsFromTo, truncate } from '~/helpers';
import messagesToast from '~/validations/messagesNotifications';
import * as NuacomComponent from '~/components';
import { PlayIcon, StopIcon, PlayIconOff } from '~/assets/svgIcons';
import { Row, LeftSide, RightSide, BoxTags, Tags } from './styles';

export default function Rows({ item, currentOpen, setCurrentOpen }) {
  // STORE
  const isIntegrated = useSelector(state => state?.integrations?.isIntegrated);

  // Item it's a calldetails object
  const details = isIntegrated ? getDetailsFromTo(item) : item;

  const {
    callId,
    duration,
    callDate,
    to,
    toName,
    from,
    fromName,
    recording,
    icon,
    discriptiveTime,
  } = details || {};

  const { timeParsed } = dateParse(duration, callDate);

  // STATES
  const [player, setPlayer] = useState(false);
  const [playerLoading, setPlayerLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  // HOOKS
  const dispatch = useDispatch();
  const [{ callRecord }, setPlayRecord] = usePlayRecord();
  const { t } = lib.useTranslation();

  // EFFECTS
  useEffect(() => {
    if (currentOpen !== callId) {
      setPlayer(false);
    }
  }, [currentOpen, callId]);

  async function playRecord(id, playerBehavior) {
    if (playerBehavior && !playerLoading) {
      setPlayerLoading(true);
      await setPlayRecord(id);
      setPlayerLoading(false);
      setCurrentOpen(id);
      setPlayer(playerBehavior);
    } else {
      setCurrentOpen(null);
    }
  }

  async function deleteCallRecording(recordCallId) {
    try {
      setPlayer(false);
      await api.calls.delete.record(recordCallId);
      dispatch(actions.updateCallRecordingStatus(recordCallId));
      lib.ToastMessage(
        'success',
        messagesToast().CALLS.callRecording.delete.success
      );
    } catch (err) {
      log.error('Failed to delete call recorder.', err);
      lib.ToastMessage(
        'error',
        messagesToast().CALLS.callRecording.delete.error
      );
    }
  }

  /**
   * This is called when the user confirm the record deletion via modal.
   */
  function acceptDeleteCallRecording(action) {
    if (action) {
      deleteCallRecording(callId);
      setOpenModal(false);
    } else {
      setOpenModal(false);
    }
  }

  function handleDelete() {
    setOpenModal(true);
  }

  const openCallDetails = data => {
    if (data) {
      dispatch(actions.setCallDetails(data));
    }
  };

  const playStopIcon = player ? StopIcon : PlayIcon;
  const truncatedFrom = fromName ? truncate(fromName, 20) : from;
  const truncatedTo = toName ? truncate(toName, 20) : to;

  return (
    <Row>
      <BoxTags>
        {details?.extra?.all_tags
          ?.filter(f => f.assigned === 1)
          .map(e => (
            <Tags key={Math.random()} color={e.tag_color} />
          ))}
      </BoxTags>

      <div className="container">
        <div style={{ flex: 1, cursor: 'pointer' }}>
          <LeftSide onClick={() => openCallDetails(details)}>
            <ReactSVG
              src={icon}
              className="icon-direction"
              title={t('components.callHistory.rows.iconDirection', {
                direction: details.directionLabel,
              })}
            />
            <div className="box-title">
              <span>{t('components.callHistory.rows.from')}</span>
              <span>{t('components.callHistory.rows.to')}</span>
            </div>
            <div className="box-values">
              <span title={from}>{truncatedFrom}</span>
              <span title={to}>{truncatedTo}</span>
            </div>
          </LeftSide>
        </div>
        <RightSide>
          <div className="box-values">
            <span>{`${timeParsed[0]}:${timeParsed[1]}`}</span>
            <span data-test-id="recording-time">
              {discriptiveTime || `${duration}s`}
            </span>
          </div>
          {playerLoading && recording && (
            <div className="player-loading-container">
              <CircularProgress />
              <IconButton
                onClick={() => playRecord(callId, !player)}
                className="btn-play-stop">
                <ReactSVG src={playStopIcon} />
              </IconButton>
            </div>
          )}

          {!recording && (
            <div className="player-off-cotnainer">
              <IconButton onClick={() => false} className="btn-play" disabled>
                <ReactSVG src={PlayIconOff} className="icon-play" />
              </IconButton>
            </div>
          )}

          {!playerLoading && recording && (
            <IconButton
              onClick={() => playRecord(callId, !player)}
              className="btn-play"
              disabled={!recording}>
              <ReactSVG
                src={recording ? playStopIcon : PlayIconOff}
                className="icon-play"
              />
            </IconButton>
          )}
        </RightSide>
      </div>
      {player && !!callRecord && (
        <NuacomComponent.Player audio={callRecord} onDelete={handleDelete} />
      )}
      <NuacomComponent.ConfirmationBox
        setOpenModal={setOpenModal}
        openModal={openModal}
        handleAction={acceptDeleteCallRecording}
      />
    </Row>
  );
}

Rows.defaultProps = {
  item: [],
  currentOpen: null,
};

Rows.propTypes = {
  item: lib.PropTypes.instanceOf(Object),
  currentOpen: lib.PropTypes.string,
  setCurrentOpen: lib.PropTypes.func.isRequired,
};
